import Campaign from '@/common/models/campaign/Campaign';
import { Filter, FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models/query-params';
import SharedPublicEntity from '@/common/models/SharedPublicEntity';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import rootStore from '@/store';
import * as modelTypes from '@/store/modules/models/mutation-types';
import {
  createApiAction,
  createAuditAction,
  createDeleteAction,
  createGetAllAction,
  createGetOneAction,
  createPostAction,
  createPutAction,
} from '@/utils/ActionsUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  createNewCampaign({ commit }, campaign = new Campaign(-1, '')) {
    commit(types.CREATE_CAMPAIGN, campaign);
  },
  getCampaigns: createGetAllAction(
    API.instance.getCampaigns,
    types.UPDATE_CAMPAIGNS_LIST,
    'messages.failedToGetCampaigns',
    true,
  ),
  getCampaign: createGetOneAction(API.instance.getCampaign, types.UPDATE_CAMPAIGN, 'messages.failedToGetCampaign'),
  getPublicSharedCampaign: createApiAction(API.instance.getPublicSharedCampaign, null, 'messages.failedToGetCampaign'),
  createCampaigns: createPostAction(
    API.instance.postCampaigns,
    types.SAVE_CAMPAIGNS,
    'messages.failedToCreateCampaign',
  ),
  updateCampaign: createPutAction(API.instance.putCampaign, types.SAVE_CAMPAIGNS, 'messages.failedToUpdateCampaign'),
  deleteCampaign: createDeleteAction(
    API.instance.deleteCampaign,
    types.DELETE_CAMPAIGN,
    'messages.failedToDeleteCampaign',
  ),
  getCampaignAudit: createAuditAction(API.instance.getCampaignAudit, 'messages.failedToGetCampaignAudit'),

  getCampaignSummaryDeliveryStats: createApiAction(
    API.instance.getCampaignSummaryDeliveryStats,
    modelTypes.UPDATE_CAMPAIGN_DELIVERY_HOURLY_STATS,
    'messages.failedToGetCampaignSummaryDeliveryStats',
    false,
    null,
    true,
  ),

  getCampaignCustomFields: createApiAction(API.instance.getCampaignCustomFields),
  getCampaignSummaryPanel: createApiAction(
    API.instance.getCampaignSummaryPanel,
    null,
    'messages.failedtoGetCampaignSummaryPanelStats',
  ),
  getPublicSharedCampaignDelivery: createApiAction(
    API.instance.getPublicSharedCampaignDelivery,
    null,
    'messages.failedtoGetCampaignDetailsStats',
  ),
  getCampaignDetails: createApiAction(
    API.instance.getCampaignDetails,
    null,
    'messages.failedtoGetCampaignDetailsStats',
  ),
  getCampaignSites: createApiAction(API.instance.getCampaignSites, null, 'messages.failedtoGetCampaignSitesStats'),
  getCampaignUnits: createApiAction(API.instance.getCampaignUnits, null, 'messages.failedtoGetCampaignUnitsStats'),

  async updateSharedCampaignPassword(_, { campaign, plan, publicPlaintextPassword, showLoader }) {
    let planInternal = plan ?? null;
    let campaignInternal = campaign ?? null;

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.enableLoader);
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    //
    // This part is for Plan only
    // Use case 1: When saving a plan as a campaign, we verify the plan has been shared. If yes, we set the same password
    // Use case 2: When sharing a plan and has already a campaign linked to it, we verify the campaign is public. If not, we set the same password
    //
    if (!planInternal && campaign?.planID) {
      planInternal = await rootStore.dispatch('orgApp/getPlan', { id: campaign.planID });
    }
    if (!campaign && planInternal) {
      const data = await rootStore.dispatch('orgApp/getCampaigns', {
        queryParams: QueryParams.for({
          filterCollection: new FilterCollection([
            new Filter('plan_id', [planInternal.id], FilterComparisonOperator.EQUAL),
          ]),
          expand: ['shared_entity'],
        }),
      });
      campaignInternal = data?.items?.length > 0 ? data.items[0] : null;
    }

    if (campaignInternal) {
      const passwordInternal = publicPlaintextPassword ?? planInternal?.sharedEntity?.publicPlaintextPassword;
      const campaignPassword = campaignInternal?.sharedEntity?.publicPlaintextPassword;

      if (passwordInternal != null && campaignPassword != passwordInternal) {
        const sharedCampaign = SharedPublicEntity.fromSharedPublicEntityApi({
          public_plaintext_password: passwordInternal,
          settings: {},
          active: true,
        });
        rootStore.dispatch('updateCampaignSharedEntity', {
          campaign: campaignInternal,
          value: sharedCampaign,
        });
        await rootStore.dispatch('orgApp/updateCampaign', { instance: campaignInternal });
      }
    }

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
      GlobalEventBus.$emit(GlobalEvents.disableLoader);
    }
  },
  getCampaignTimeline({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getCampaignTimeline(
        params,
        (stats) => {
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetCampaignTimelineStats');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },
  extendPublicCampaign: createApiAction(
    API.instance.extendPublicCampaign,
    null,
    'messages.failedToExtendPublicCampaign',
  ),
};

export default actions;
